import { useEffect, useState } from 'react';

const ACCESS_TOKEN_COOKIE = 'rhaccesstoken';
export const AUTH_CHANGE_EVENT = 'rh-auth-change';

/**
 * Reads a single cookie value by name.
 */
export function getCookie(
  name,
  cookieString = typeof document !== 'undefined' ? document.cookie : '',
) {
  const prefix = `${name}=`;
  const match = decodeURI(cookieString)
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(prefix));

  return match ? match.slice(prefix.length) : null;
}

/**
 * Reads the auth access token from cookies.
 */
export function getAccessTokenFromCookies(
  cookieString = typeof document !== 'undefined' ? document.cookie : '',
) {
  return getCookie(ACCESS_TOKEN_COOKIE, cookieString);
}

/**
 * Parent cookie domain shared across subdomains, e.g.
 * offer.rafflehouse.com → .rafflehouse.com
 * Skips localhost / IPs (no Domain attribute).
 */
function getSharedCookieDomain(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
) {
  if (
    !hostname ||
    hostname === 'localhost' ||
    hostname.endsWith('.local') ||
    /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
  ) {
    return null;
  }

  const parts = hostname.split('.').filter(Boolean);
  if (parts.length < 2) return null;

  // Root domain without subdomain: offer.rafflehouse.com → .rafflehouse.com
  return `.${parts.slice(-2).join('.')}`;
}

/**
 * Matches main-site cookie shape: name, value, path=/, domain=.rafflehouse.com, 7 days.
 * refreshToken is set by the API (HttpOnly) — do not write it from the client.
 */
function buildAccessTokenCookie(value, maxAgeSeconds = 60 * 60 * 24 * 7) {
  const domain = getSharedCookieDomain();
  const domainPart = domain ? `; domain=${domain}` : '';

  return `${ACCESS_TOKEN_COOKIE}=${value}; path=/${domainPart}; max-age=${maxAgeSeconds}`;
}

/**
 * Persists the access token cookie and notifies listeners.
 */
export function setAuthTokens(token) {
  if (typeof document === 'undefined') return;

  if (token) {
    document.cookie = buildAccessTokenCookie(token);
  }

  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
}

/**
 * Clears the access token cookie and notifies listeners.
 */
export function clearAuthTokens() {
  if (typeof document === 'undefined') return;

  const domain = getSharedCookieDomain();
  const domainPart = domain ? `; domain=${domain}` : '';

  document.cookie = `${ACCESS_TOKEN_COOKIE}=; path=/${domainPart}; max-age=0`;
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
}

/**
 * Checks whether the user is logged in via the `rhaccesstoken` cookie.
 * Independent of this landing — no project-specific imports.
 */
export default function useIsLoggedIn() {
  const [isLoggedIn, setIsLoggedIn] = useState(() =>
    Boolean(getAccessTokenFromCookies()),
  );

  useEffect(() => {
    const checkAuth = () => {
      setIsLoggedIn(Boolean(getAccessTokenFromCookies()));
    };

    checkAuth();
    window.addEventListener('focus', checkAuth);
    window.addEventListener(AUTH_CHANGE_EVENT, checkAuth);

    return () => {
      window.removeEventListener('focus', checkAuth);
      window.removeEventListener(AUTH_CHANGE_EVENT, checkAuth);
    };
  }, []);

  return isLoggedIn;
}
